/**
 * Slash commands of the composer (QW-113, QW-1211): "/" opens a menu of at
 * most 50 commands; typing narrows it by fuzzy match; Enter takes the
 * selected command into the box instead of sending. A command the app does
 * not know is sent as an ordinary message.
 */
export const SLASH_COMMANDS = ['compress', 'clear', 'rewind', 'export', 'btw', 'model', 'memory', 'help', 'title', 'branch', 'fork', 'recap', 'context', 'skill'] as const;
export type SlashCommand = (typeof SLASH_COMMANDS)[number];

export const MENU_LIMIT = 50;

/** The commands that work before there is a conversation (the home page's composer); the rest act on one. */
export const HOME_COMMANDS: readonly SlashCommand[] = ['model', 'memory', 'help', 'skill'];

/** Commands that need text after them. */
export const TAKES_ARGUMENT: readonly SlashCommand[] = ['btw', 'title', 'export', 'fork', 'skill'];

/** Higher is better; null when the letters do not appear in order. */
export function fuzzyScore(query: string, candidate: string): number | null {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (!q) return 0;
  if (c.startsWith(q)) return 1000 - c.length;
  let score = 0;
  let at = 0;
  let previous = -2;
  for (const ch of q) {
    const found = c.indexOf(ch, at);
    if (found < 0) return null;
    score += found === previous + 1 ? 10 : 1;
    previous = found;
    at = found + 1;
  }
  return score - c.length / 100;
}

export function matchCommands<T extends string>(query: string, commands: readonly T[] = SLASH_COMMANDS as unknown as T[]): T[] {
  return commands
    .map((command) => ({ command, score: fuzzyScore(query, command) }))
    .filter((entry): entry is { command: T; score: number } => entry.score !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, MENU_LIMIT)
    .map((entry) => entry.command);
}

/** An entry of the "/" menu: a built-in command or a skill the user may run there. */
export type SlashEntry = { kind: 'command'; name: SlashCommand } | { kind: 'skill'; name: string; description: string };

/**
 * The menu's entries, best match first: skills and commands are ranked
 * together, so the top entry is the highlighted one Enter takes. Equal
 * scores (a bare "/") keep the skills before the commands.
 */
export function rankEntries(query: string, commands: readonly SlashCommand[], skills: readonly { name: string; description: string }[]): SlashEntry[] {
  const entries: SlashEntry[] = [...skills.map((s) => ({ kind: 'skill' as const, name: s.name, description: s.description })), ...commands.map((name) => ({ kind: 'command' as const, name }))];
  return entries
    .map((entry, order) => ({ entry, order, score: fuzzyScore(query, entry.name) }))
    .filter((x): x is { entry: SlashEntry; order: number; score: number } => x.score !== null)
    .sort((a, b) => b.score - a.score || a.order - b.order)
    .slice(0, MENU_LIMIT)
    .map((x) => x.entry);
}

/**
 * The menu for a query (`menuQuery` of the box): none without one, and none
 * once a whole command or skill name is in the box, so the next Enter runs it.
 */
export function menuEntries(query: string | null, commands: readonly SlashCommand[], skills: readonly { name: string; description: string }[]): SlashEntry[] {
  if (query === null || (SLASH_COMMANDS as readonly string[]).includes(query) || skills.some((s) => s.name === query)) return [];
  return rankEntries(query, commands, skills);
}

/** The box after Enter takes an entry: a skill leaves room for its request, some commands for their argument (QW-113, QW-1205). */
export function entryText(entry: SlashEntry): string {
  if (entry.kind === 'skill') return `/${entry.name} `;
  return `/${entry.name}${TAKES_ARGUMENT.includes(entry.name) ? ' ' : ''}`;
}

/**
 * Enters pressed before the home page knew where a new conversation goes and
 * which skills it has there (just after a restart), played back once it
 * does, each as it would have gone then: with a menu open it takes the top
 * entry, else it sends. `held` is the box at each Enter and `now` the box
 * now; what was typed after an Enter follows its result. An Enter that would
 * send while more was typed after it does not: the writer was not done, and
 * nothing typed is lost. The page keeps only Enters the writer typed on
 * from; should one not lead to the box as it is, none is played back.
 */
export function replayEnters(now: string, held: readonly string[], complete: (text: string) => string | null): { text: string; send: boolean } {
  if (!held.every((at, index) => now.startsWith(at) && at.length >= (held[index - 1]?.length ?? 0))) return { text: now, send: false };
  let text = '';
  let typed = '';
  for (const at of held) {
    text += at.slice(typed.length);
    typed = at;
    const picked = complete(text);
    if (picked !== null) text = picked;
    else if (typed.length === now.length) return { text, send: true };
  }
  return { text: text + now.slice(typed.length), send: false };
}

/** "/btw what is …" → { command: 'btw', argument: 'what is …' }; null when the text is not a known command. */
export function parseSlash(text: string): { command: SlashCommand; argument: string } | null {
  const match = /^\/([a-z]+)(?:\s+([\s\S]*))?$/.exec(text.trim());
  if (!match) return null;
  const command = match[1] as SlashCommand;
  if (!SLASH_COMMANDS.includes(command)) return null;
  return { command, argument: (match[2] ?? '').trim() };
}

/** The menu shows while the box holds only "/" and the start of a command or skill name. */
export function menuQuery(text: string): string | null {
  const match = /^\/([a-z0-9-]*)$/.exec(text);
  return match ? (match[1] as string) : null;
}

/** `/skill add <source>` (D-13): the source to preview, or '' when none was given. */
export function skillAddSource(argument: string): string | null {
  const match = /^(?:add|ekle)(?:\s+([\s\S]*))?$/i.exec(argument.trim());
  if (match) return (match[1] ?? '').trim();
  return argument.trim() === '' ? '' : null;
}
